/**
 * Ambient battlefield atmosphere: airborne motes, biome weather and slow
 * light shafts drifting over the field.
 *
 * The particles are allocated once at attach() and recycled by wrapping them
 * back to the opposite edge when they leave the play area, so the layer costs
 * nothing per frame beyond the integration itself. Two depth bands give a
 * cheap parallax read: a fine background haze behind the units and a sparser,
 * larger foreground drift in front of them.
 *
 * Lifecycle: GameScene calls atmosphere.attach(this, biome) in create() and
 * atmosphere.update(delta) from update(). Pass the REAL delta — weather
 * shouldn't sprint when the player hits 4x.
 */
import Phaser from "phaser";
import { GAME_WIDTH, GRID_ROWS, MAP_OFFSET_Y, TILE_SIZE } from "../config";
import { Biome } from "../maps/maps";

/** Vertical band the weather occupies — the battlefield, not the HUD bars. */
const FIELD_TOP = MAP_OFFSET_Y;
const FIELD_BOTTOM = MAP_OFFSET_Y + GRID_ROWS * TILE_SIZE;
const FIELD_HEIGHT = FIELD_BOTTOM - FIELD_TOP;

interface Profile {
  /** Particles in the background haze band. */
  backCount: number;
  /** Particles in the foreground parallax band. */
  frontCount: number;
  color: number;
  sizeMin: number;
  sizeMax: number;
  alphaMin: number;
  alphaMax: number;
  /** Drift velocity range in px/s. */
  vxMin: number;
  vxMax: number;
  vyMin: number;
  vyMax: number;
  /** Horizontal sine sway amplitude in px — 0 for straight-line weather. */
  sway: number;
  additive: boolean;
  /** Number of slow god-ray shafts. 0 disables them. */
  shafts: number;
  shaftColor: number;
  shaftAlpha: number;
  /** Shaft tilt in radians from vertical. */
  shaftTilt: number;
}

const PROFILES: Record<Biome, Profile> = {
  // Warm pollen and dust catching low sun.
  plains: {
    backCount: 40, frontCount: 10, color: 0xffe9b0,
    sizeMin: 1.4, sizeMax: 3.0, alphaMin: 0.16, alphaMax: 0.40,
    vxMin: 6, vxMax: 20, vyMin: -9, vyMax: -2, sway: 10, additive: true,
    shafts: 3, shaftColor: 0xffeec0, shaftAlpha: 0.055, shaftTilt: 0.30
  },
  // River mist: cool, slow, hugging the water.
  river: {
    backCount: 34, frontCount: 8, color: 0xd8ecff,
    sizeMin: 2.0, sizeMax: 4.4, alphaMin: 0.12, alphaMax: 0.30,
    vxMin: 8, vxMax: 22, vyMin: -5, vyMax: 2, sway: 7, additive: true,
    shafts: 2, shaftColor: 0xd8ecff, shaftAlpha: 0.045, shaftTilt: 0.22
  },
  // Swamp spores: heavy, green, barely moving.
  swamp: {
    backCount: 46, frontCount: 12, color: 0xa8d878,
    sizeMin: 1.6, sizeMax: 3.8, alphaMin: 0.14, alphaMax: 0.34,
    vxMin: 2, vxMax: 11, vyMin: -12, vyMax: -3, sway: 14, additive: true,
    shafts: 2, shaftColor: 0xbce098, shaftAlpha: 0.05, shaftTilt: 0.16
  },
  // Snowfall: dense, white, straight down with a light crosswind.
  snow: {
    backCount: 70, frontCount: 18, color: 0xffffff,
    sizeMin: 1.8, sizeMax: 3.6, alphaMin: 0.30, alphaMax: 0.75,
    vxMin: -22, vxMax: -6, vyMin: 26, vyMax: 60, sway: 12, additive: false,
    shafts: 0, shaftColor: 0xffffff, shaftAlpha: 0, shaftTilt: 0
  },
  // Mine embers rising off unseen forges.
  mine: {
    backCount: 30, frontCount: 12, color: 0xff9a40,
    sizeMin: 1.2, sizeMax: 2.6, alphaMin: 0.25, alphaMax: 0.65,
    vxMin: -6, vxMax: 8, vyMin: -34, vyMax: -14, sway: 9, additive: true,
    shafts: 2, shaftColor: 0xffb060, shaftAlpha: 0.06, shaftTilt: 0.10
  },
  // Desert sand streaking on a hot wind.
  desert: {
    backCount: 52, frontCount: 14, color: 0xf0d9a0,
    sizeMin: 1.2, sizeMax: 2.8, alphaMin: 0.18, alphaMax: 0.44,
    vxMin: 46, vxMax: 104, vyMin: -6, vyMax: 5, sway: 4, additive: false,
    shafts: 2, shaftColor: 0xffeec0, shaftAlpha: 0.05, shaftTilt: 0.38
  },
  // Siege: ash drifting down through smoke.
  siege: {
    backCount: 56, frontCount: 16, color: 0xc8bcae,
    sizeMin: 1.4, sizeMax: 3.4, alphaMin: 0.20, alphaMax: 0.50,
    vxMin: -16, vxMax: 6, vyMin: 12, vyMax: 34, sway: 11, additive: false,
    shafts: 3, shaftColor: 0xffd0a0, shaftAlpha: 0.05, shaftTilt: 0.26
  }
};

interface Mote {
  obj: Phaser.GameObjects.Ellipse;
  vx: number;
  vy: number;
  /** Base X the sway oscillates around. */
  baseX: number;
  swayAmp: number;
  swayPhase: number;
  swaySpeed: number;
  baseAlpha: number;
  /** Depth band multiplier — foreground motes move faster and look bigger. */
  parallax: number;
}

interface Shaft {
  obj: Phaser.GameObjects.Rectangle;
  vx: number;
  baseAlpha: number;
  phase: number;
  pulseSpeed: number;
}

class AtmosphereSystem {
  private scene?: Phaser.Scene;
  private profile: Profile = PROFILES.plains;
  private motes: Mote[] = [];
  private shafts: Shaft[] = [];
  private timeSec = 0;
  /** Global density/opacity multiplier — raised on boss waves. */
  private intensity = 1;
  private targetIntensity = 1;

  get particleCount(): number { return this.motes.length + this.shafts.length; }

  attach(scene: Phaser.Scene, biome: Biome): void {
    this.detach();
    this.scene = scene;
    this.profile = PROFILES[biome] ?? PROFILES.plains;
    this.timeSec = 0;
    this.intensity = 1;
    this.targetIntensity = 1;

    const p = this.profile;
    for (let i = 0; i < p.backCount; i++) this.motes.push(this.makeMote(scene, false));
    for (let i = 0; i < p.frontCount; i++) this.motes.push(this.makeMote(scene, true));
    for (let i = 0; i < p.shafts; i++) this.shafts.push(this.makeShaft(scene, i));

    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.detach());
  }

  detach(): void {
    // Phaser destroys the GameObjects with the scene; just drop our refs.
    this.motes = [];
    this.shafts = [];
    this.scene = undefined;
  }

  /**
   * Thicken or thin the weather. Boss waves push toward ~1.5 for a heavier,
   * more oppressive field; it eases rather than snapping.
   */
  setIntensity(v: number): void {
    this.targetIntensity = Phaser.Math.Clamp(v, 0.5, 2);
  }

  /** Advance the weather. Pass the REAL delta — 4x shouldn't fast-forward it. */
  update(deltaMs: number): void {
    if (!this.scene) return;
    const dt = deltaMs / 1000;
    this.timeSec += dt;

    // Ease intensity so boss-wave transitions read as a slow gathering.
    this.intensity += (this.targetIntensity - this.intensity) * Math.min(1, dt * 0.8);

    for (const m of this.motes) {
      m.baseX += m.vx * dt * m.parallax;
      m.obj.y += m.vy * dt * m.parallax;

      // Horizontal sway keeps motes from reading as a rigid particle grid.
      m.swayPhase += m.swaySpeed * dt;
      m.obj.x = m.baseX + Math.sin(m.swayPhase) * m.swayAmp;
      m.obj.alpha = m.baseAlpha * this.intensity;

      // Wrap on whichever edge the drift carries it past.
      if (m.baseX < -20) m.baseX = GAME_WIDTH + 20;
      else if (m.baseX > GAME_WIDTH + 20) m.baseX = -20;
      if (m.obj.y < FIELD_TOP - 20) {
        m.obj.y = FIELD_BOTTOM + 20;
        m.baseX = Math.random() * GAME_WIDTH;
      } else if (m.obj.y > FIELD_BOTTOM + 20) {
        m.obj.y = FIELD_TOP - 20;
        m.baseX = Math.random() * GAME_WIDTH;
      }
    }

    for (const s of this.shafts) {
      s.obj.x += s.vx * dt;
      s.phase += s.pulseSpeed * dt;
      // Breathe the shafts so the light feels alive rather than painted on.
      s.obj.alpha = s.baseAlpha * (0.65 + 0.35 * Math.sin(s.phase)) * this.intensity;
      const halfW = s.obj.width;
      if (s.vx > 0 && s.obj.x > GAME_WIDTH + halfW) s.obj.x = -halfW;
      else if (s.vx < 0 && s.obj.x < -halfW) s.obj.x = GAME_WIDTH + halfW;
    }
  }

  private makeMote(scene: Phaser.Scene, foreground: boolean): Mote {
    const p = this.profile;
    const parallax = foreground ? 1.7 : 1;
    const sizeScale = foreground ? 1.6 : 1;
    const size = (p.sizeMin + Math.random() * (p.sizeMax - p.sizeMin)) * sizeScale;
    const baseX = Math.random() * GAME_WIDTH;
    const y = FIELD_TOP + Math.random() * FIELD_HEIGHT;
    const baseAlpha = (p.alphaMin + Math.random() * (p.alphaMax - p.alphaMin)) * (foreground ? 0.8 : 1);

    const obj = scene.add.ellipse(baseX, y, size, size, p.color, 1)
      .setDepth(foreground ? 990 : 5)
      .setAlpha(baseAlpha);
    if (p.additive) obj.setBlendMode(Phaser.BlendModes.ADD);

    return {
      obj,
      vx: p.vxMin + Math.random() * (p.vxMax - p.vxMin),
      vy: p.vyMin + Math.random() * (p.vyMax - p.vyMin),
      baseX,
      swayAmp: p.sway * (0.4 + Math.random() * 0.6),
      swayPhase: Math.random() * Math.PI * 2,
      swaySpeed: 0.3 + Math.random() * 0.7,
      baseAlpha,
      parallax
    };
  }

  private makeShaft(scene: Phaser.Scene, index: number): Shaft {
    const p = this.profile;
    const w = 90 + Math.random() * 130;
    const x = (GAME_WIDTH / Math.max(1, p.shafts)) * index + Math.random() * 120;
    const obj = scene.add.rectangle(x, FIELD_TOP + FIELD_HEIGHT / 2, w, FIELD_HEIGHT * 1.8, p.shaftColor, 1)
      .setDepth(650)
      .setAlpha(p.shaftAlpha)
      .setBlendMode(Phaser.BlendModes.ADD);
    obj.setRotation(p.shaftTilt);

    return {
      obj,
      vx: 3 + Math.random() * 6,
      baseAlpha: p.shaftAlpha,
      phase: Math.random() * Math.PI * 2,
      pulseSpeed: 0.15 + Math.random() * 0.25
    };
  }
}

export const atmosphere = new AtmosphereSystem();
